import { forwardRef, HTMLProps } from 'react';
import styled from 'styled-components';
import useForkRef from '@mui/utils/useForkRef';
import { useListboxVirtualizer } from 'app/shared/u21-ui/components/input/select/useListboxVirtualizer';

export interface VirtualizedListboxProps extends HTMLProps<HTMLUListElement> {
  highlightedIndex?: number;
  estimateSize?: number;
  overscan?: number;
}

export const VirtualizedListbox = forwardRef<
  HTMLUListElement,
  VirtualizedListboxProps
>((props, ref) => {
  const {
    children,
    estimateSize,
    highlightedIndex = -1,
    overscan,
    ...rest
  } = props;

  const { childrenArray, setListRef, virtualizer } = useListboxVirtualizer({
    children,
    estimateSize,
    highlightedIndex,
    overscan,
  });

  const mergedRef = useForkRef(ref, setListRef);

  return (
    <StyledMenu ref={mergedRef} {...rest}>
      <StyledVirtualContainer $height={virtualizer.getTotalSize()}>
        {virtualizer.getVirtualItems().map((item) => (
          <VirtualItem
            key={item.key}
            ref={virtualizer.measureElement}
            data-index={item.index}
            $translateY={item.start}
            role="presentation"
          >
            {childrenArray[item.index]}
          </VirtualItem>
        ))}
      </StyledVirtualContainer>
    </StyledMenu>
  );
});

const StyledMenu = styled.ul`
  padding: ${({ theme }) => theme.spacing(1)};
  overflow-y: auto;
`;

const StyledVirtualContainer = styled.div<{ $height: number }>`
  height: ${({ $height }) => $height}px;
  position: relative;
`;

const VirtualItem = styled.div<{ $translateY: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  transform: translateY(${({ $translateY }) => $translateY}px);
`;
